import { useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import MainLayout from './components/Layout/MainLayout'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import AnnotationWorkspace from './pages/Annotation/AnnotationWorkspace'
import ReviewWorkspace from './pages/Review/ReviewWorkspace'
import ReviewHistory from './pages/Review/ReviewHistory'
import AnnotationReviewResults from './pages/Annotation/AnnotationReviewResults'
import UploadManager from './pages/Upload/UploadManager'
import DataUpload from './pages/DataUpload'
import CaseSearch from './pages/CaseSearch'
import KnowledgeGraph from './pages/KnowledgeGraph'
import UserManagement from './pages/Admin/UserManagement'
import TagConfig from './pages/Admin/TagConfig'
import SystemSettings from './pages/Admin/SystemSettings'
import OperationLogs from './pages/Admin/OperationLogs'
import SystemMonitor from './pages/Admin/SystemMonitor'
import DatasetDownload from './pages/Admin/DatasetDownload'
import ApiService from './pages/Admin/ApiService'
import ModelFinetune from './pages/Admin/ModelFinetune'
import { useAuthStore } from './stores/auth'
import { getAuthData } from './utils/auth'
import './App.css'



// 路由守卫组件
const PrivateRoute = ({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) => {
  const { isAuthenticated, user, checkAuth } = useAuthStore()
  const [isLoading, setIsLoading] = useState(true)
  
  // 组件加载时检查认证状态
  useEffect(() => {
    checkAuth()
    // 短暂延迟确保状态更新
    const timer = setTimeout(() => setIsLoading(false), 100)
    return () => clearTimeout(timer)
  }, [checkAuth])
  
  // 等待认证检查完成
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-500 dark:text-slate-400">加载中...</p>
        </div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <MainLayout />
          </PrivateRoute>
        }
      >
        {/* 工作台 - 所有角色 */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* 标注工作台 - 标注员/管理员 */}
        <Route
          path="annotation"
          element={
            <PrivateRoute allowedRoles={['annotator', 'admin']}>
              <AnnotationWorkspace />
            </PrivateRoute>
          }
        />
        <Route
          path="annotation/:caseId"
          element={
            <PrivateRoute allowedRoles={['annotator', 'admin']}>
              <AnnotationWorkspace />
            </PrivateRoute>
          }
        />
        
        {/* 审核工作台 - 审核员/管理员 */}
        <Route
          path="review"
          element={
            <PrivateRoute allowedRoles={['reviewer', 'admin']}>
              <ReviewWorkspace />
            </PrivateRoute>
          }
        />
        <Route
          path="review/:caseId"
          element={
            <PrivateRoute allowedRoles={['reviewer', 'admin']}>
              <ReviewWorkspace />
            </PrivateRoute>
          }
        />
        <Route
          path="review/history"
          element={
            <PrivateRoute allowedRoles={['reviewer', 'admin']}>
              <ReviewHistory />
            </PrivateRoute>
          }
        />
        <Route
          path="annotation/review-results"
          element={
            <PrivateRoute allowedRoles={['annotator', 'admin']}>
              <AnnotationReviewResults />
            </PrivateRoute>
          }
        />
        
        {/* 数据上传清洗 - 管理员/标注员/审核员 */}
        <Route
          path="upload-clean"
          element={
            <PrivateRoute allowedRoles={['admin', 'annotator', 'reviewer']}>
              <DataUpload />
            </PrivateRoute>
          }
        />
        
        {/* 批量上传 - 仅管理员 (保留旧版兼容) */}
        <Route
          path="upload"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <UploadManager />
            </PrivateRoute>
          }
        />
        
        {/* 案例查询 - 所有角色 */}
        <Route
          path="case-search"
          element={
            <PrivateRoute allowedRoles={['query_user', 'admin', 'annotator', 'reviewer']}>
              <CaseSearch />
            </PrivateRoute>
          }
        />
        
        {/* 知识图谱 - 信息查询员/管理员 */}
        <Route
          path="knowledge-graph"
          element={
            <PrivateRoute allowedRoles={['query_user', 'admin', 'annotator', 'reviewer']}>
              <KnowledgeGraph />
            </PrivateRoute>
          }
        />
        {/* 兼容旧路由 */}
        <Route
          path="graph"
          element={
            <PrivateRoute allowedRoles={['query_user', 'admin', 'annotator', 'reviewer']}>
              <KnowledgeGraph />
            </PrivateRoute>
          }
        />
        
        {/* 系统管理 - 管理员 */}
        <Route
          path="admin"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <Navigate to="/admin/users" replace />
            </PrivateRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <UserManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="admin/tags"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <TagConfig />
            </PrivateRoute>
          }
        />
        <Route
          path="admin/settings"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <SystemSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="admin/logs"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <OperationLogs />
            </PrivateRoute>
          }
        />
        <Route
          path="admin/monitor"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <SystemMonitor />
            </PrivateRoute>
          }
        />
        
        {/* 高级功能 - 管理员专属 */}
        <Route
          path="admin/dataset"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <DatasetDownload />
            </PrivateRoute>
          }
        />
        <Route
          path="admin/api"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <ApiService />
            </PrivateRoute>
          }
        />
        <Route
          path="admin/model"
          element={
            <PrivateRoute allowedRoles={['admin']}>
              <ModelFinetune />
            </PrivateRoute>
          }
        />
      </Route>
    </Routes>
  )
}

export default App
